import { useState } from 'react';
import * as Yup from 'yup';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Stack, Alert, IconButton, InputAdornment, Typography, Link } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// auth
import { useNavigate } from 'react-router-dom';
import { PATH_AUTH } from '../../routes/paths';
import { useAuthContext } from '../../auth/useAuthContext';
// components
import Iconify from '../../components/iconify';
import FormProvider, { RHFTextField } from '../../components/hook-form';
import TwoFactorChallengeForm from './TwoFactorChallengeForm';

// ----------------------------------------------------------------------

export default function AuthLoginForm() {
  const navigate = useNavigate();

  const { login } = useAuthContext();

  const [showPassword, setShowPassword] = useState(false);

  // Set when /auth/login answers with a challenge instead of a session. The
  // password form is replaced rather than hidden, so there is no half-signed-in
  // state to reason about.
  const [challenge, setChallenge] = useState(null);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  const onSubmit = async (data) => {
    try {
      const result = await login(data.email, data.password);

      // Password accepted, second step owed. login() has written nothing to
      // storage, so abandoning here leaves no partial session behind.
      if (result?.twoFactorRequired) {
        setChallenge(result);
        return;
      }
    } catch (error) {
      console.error(error);

      reset();

      setError('afterSubmit', {
        ...error,
        message: error.message,
      });

      console.log({
        ...error,
        message: error.message,
      });
    }
  };

  const handleCancelChallenge = () => {
    setChallenge(null);
    reset();
  };

  const handleRegisterRoute = () => {
    navigate(PATH_AUTH.register);
  };

  if (challenge) {
    return <TwoFactorChallengeForm challenge={challenge} onCancel={handleCancelChallenge} />;
  }

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3} sx={{ mb: 3 }}>
        {!!errors.afterSubmit && (
          <Alert severity="error">{JSON.stringify(errors.afterSubmit.message)}</Alert>
        )}

        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* <Stack alignItems="flex-end" sx={{ my: 2 }}>
        <Link variant="body2" color="inherit" underline="always">
          Forgot password?
        </Link>
      </Stack> */}

      <LoadingButton
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitSuccessful || isSubmitting}
        sx={{
          bgcolor: 'text.primary',
          color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          '&:hover': {
            bgcolor: 'text.primary',
            color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          },
        }}
      >
        Login
      </LoadingButton>
      <Typography sx={{ textAlign: 'center', mt: 2 }}>
        Not registered?{' '}
        <Link
          variant="body2"
          color="primary"
          underline="always"
          sx={{
            textDecoration: 'none',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
          onClick={handleRegisterRoute}
        >
          Create an account
        </Link>
      </Typography>
    </FormProvider>
  );
}
